import { inject, Injectable, Injector } from '@angular/core';
import { assert } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { lastValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { KeyboardShortcutService } from '../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import { ToolsHelperService } from '../../../cross-team-shared/misc-services/tools-helper.service';
import { shellConnectorToken } from '../../../team-apis/core/shell-connector';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { CuboidTool3D } from '../../tools/cuboid-tool/cuboid-tool-3d';
import { ToolManager3DService } from '../../tools/tool-manager-3d.service';

@Injectable()
export class RedrawService extends InjectableSuperclass {
  protected store = inject(PointCloudContentStore);
  private keyboardShortcutService = inject(KeyboardShortcutService);
  private injector = inject(Injector);
  private shellConnector = inject(shellConnectorToken);
  private toolManager3DService = this.injector.get(ToolManager3DService);
  private toolsHelperService = inject(ToolsHelperService);

  constructor() {
    super();
    this.subscribeTo(
      this.keyboardShortcutService
        .getMatchingEvent$('redraw')
        .pipe(filter(() => this.canRedraw())),
      async ({ keyboardEvent }) => {
        keyboardEvent.preventDefault();
        await this.redraw();
      },
    );
  }

  async redraw() {
    const focusedShapeStore = this.store.getFocusedShapeStore();
    assert(
      focusedShapeStore,
      'getFocusedShapeStore() always exist when redraw() is called',
    );
    const oldToolName = this.store.state().selectedTool;

    const tool = this.toolManager3DService.getTool('Cuboid');
    assert(tool instanceof CuboidTool3D, 'redraw tool must be CuboidTool3D');

    this.store.batch(() => {
      this.store('selectedTool').set(tool.name);
      tool.startRedraw(focusedShapeStore);
    });

    await lastValueFrom(tool.drawShapeFinished$.pipe(take(1)));

    this.store('selectedTool').set(oldToolName);
    this.shellConnector.modifyShapeSelection([focusedShapeStore.state().index]);
  }

  canRedraw() {
    if (
      this.store.state().selectedTool !== 'Edit' ||
      !this.store.getFocusedShapeStore() ||
      this.store.state().readOnly
    ) {
      return false;
    }

    return this.toolsHelperService
      .getAllowedTools(this.store.state().output.name)
      .includes('Cuboid');
  }
}
